<template>
  <div class="bg-gray-50">
    <div class="flex bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style="height: calc(100vh - 120px);">
    <!-- 第1栏：页面列表 -->
    <div class="w-64 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
      <div class="px-3 h-11 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-500 uppercase relative inline-block">
          售后二维码
          <span class="absolute -top-2 -right-6 inline-flex items-center px-1 py-0 rounded-sm text-[8px] font-bold bg-gradient-to-r from-purple-500 to-indigo-600 text-white leading-tight">PRO</span>
        </h3>
        <div class="flex items-center gap-1">
          <input v-model="searchQuery" type="text" placeholder="搜索..." class="w-28 px-1.5 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent" />
          <button @click="showCreateModal = true" class="p-1 text-blue-600 hover:text-blue-800"><PlusIcon class="h-4 w-4" /></button>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto">
        <div v-if="loading" class="p-4"><div class="animate-pulse space-y-2"><div class="h-4 bg-gray-200 rounded"></div><div class="h-4 bg-gray-200 rounded w-3/4"></div></div></div>
        <div v-else-if="allPages.length === 0" class="p-6 text-center"><QrCodeIcon class="mx-auto h-8 w-8 text-gray-300 mb-2" /><p class="text-xs text-gray-400">{{ searchQuery ? '没有匹配的页面' : '暂无页面' }}</p></div>
        <div v-else class="divide-y divide-gray-100">
          <div v-for="page in allPages" :key="page.id" class="group px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors relative" :class="{ 'bg-blue-50': currentPage?.id === page.id, 'opacity-50 pointer-events-none': loadingPageId && loadingPageId !== page.id }" @click="selectPage(page)">
            <div v-if="loadingPageId === page.id" class="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center z-10">
              <div class="animate-spin h-5 w-5 text-blue-600">
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-gray-900 truncate">{{ page.name }}</span>
              <div class="flex items-center gap-1.5 flex-shrink-0">
                <span :class="page.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="text-[10px] px-1.5 py-0.5 rounded-full">{{ page.is_active ? '启用' : '停用' }}</span>
                <button @click.stop="duplicatePage(page)" class="text-gray-400 hover:text-blue-600 transition-colors" title="复制页面">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </button>
                <button v-if="page.can_delete !== false" @click.stop="deletePage(page)" class="text-gray-400 hover:text-red-600 transition-colors" title="删除页面">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" /></svg>
                </button>
              </div>
            </div>
            <div class="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
              <span>批次:{{ page.batch_count }}</span><span>扫码:{{ page.total_scans }}</span>
              <span v-if="page.created_by_email" class="truncate" :title="page.created_by_email">· {{ page.created_by_email }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="listTotalCount > 0" class="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <span class="text-[10px] text-gray-500">共 {{ listTotalCount }} 个</span>
        <div v-if="listTotalPages > 1" class="flex items-center gap-1">
          <button type="button" @click="goListPage(listPage - 1)" :disabled="listPage <= 1 || loading" class="px-1.5 py-0.5 text-[10px] border border-gray-300 rounded hover:bg-white disabled:opacity-40">上一页</button>
          <span class="text-[10px] text-gray-600 min-w-[36px] text-center">{{ listPage }}/{{ listTotalPages }}</span>
          <button type="button" @click="goListPage(listPage + 1)" :disabled="listPage >= listTotalPages || loading" class="px-1.5 py-0.5 text-[10px] border border-gray-300 rounded hover:bg-white disabled:opacity-40">下一页</button>
        </div>
      </div>
    </div>

    <!-- 第2栏：页面设置 -->
    <div class="w-80 border-r border-gray-200 flex flex-col flex-shrink-0">
      <div class="px-3 h-11 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-500 uppercase">页面信息</h3>
        <button v-if="currentPage" @click="savePage" class="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">保存</button>
      </div>
      <div v-if="!currentPage" class="flex-1 flex items-center justify-center text-gray-400 text-sm">选择左侧页面</div>
      <div v-else class="flex-1 overflow-y-auto flex flex-col">
        <!-- 页面信息 tab 切换 -->
        <div class="flex border-b border-gray-200 bg-white flex-shrink-0">
          <button @click="activeTab='basic'" :class="activeTab==='basic' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'" class="flex-1 py-2 text-xs font-medium">基本信息</button>
          <button @click="activeTab='risk'" :class="activeTab==='risk' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'" class="flex-1 py-2 text-xs font-medium">风控管理</button>
        </div>
        <div v-show="activeTab==='basic'" class="flex-1 overflow-y-auto p-3 space-y-3">
        <div><ConfigHelpLabel label="页面名称" help-key="page_name" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="editForm.name" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" /></div>
        <div><ConfigHelpLabel label="Amazon ASIN" help-key="amazon_asin" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="editForm.amazon_asin" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="B08XXXXXXX" /></div>
        <div>
          <ConfigHelpLabel help-key="review_link" label-class="block text-sm font-medium text-gray-700 mb-2">
            <template #label>留评链接</template>
            <button v-if="editForm.amazon_asin" @click="editForm.review_link = `https://www.amazon.com/review/create-review?&asin=${editForm.amazon_asin}:5`" class="text-xs text-blue-600 hover:text-blue-800 font-normal underline-offset-2 hover:underline">快速生成</button>
          </ConfigHelpLabel>
          <input v-model="editForm.review_link" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="https://www.amazon.com/review/..." />
        </div>
        <div><ConfigHelpLabel label="验证码" help-key="verification_code" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="editForm.verification_code" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="1688" /><p class="text-xs text-gray-400 mt-1">买家回复此验证码完成建联</p></div>
        <div>
          <ConfigHelpLabel label="客服邮箱" help-key="sender_email_account" label-class="block text-sm font-medium text-gray-700 mb-2" />
          <SearchableSelect v-model="editForm.sender_email_account" :options="emailAccountOptions" placeholder="-- 请选择 --" label-key="email" value-key="id" :clearable="true" />
          <p class="text-xs text-gray-400 mt-1">用于发送验证邮件和建联成功邮件</p>
        </div>

        <div>
          <ConfigHelpLabel label="客户分组" help-key="customer_group" label-class="block text-sm font-medium text-gray-700 mb-2" />
          <SearchableSelect v-model="editForm.customer_group" :options="customerGroupOptions" placeholder="默认分组（二维码PRO客户）" label-key="name" value-key="id" :clearable="true" />
          <p class="text-xs text-gray-400 mt-1">订阅的客户将归入所选分组</p>
        </div>
        <div><ConfigHelpLabel label="客户来源" help-key="customer_source" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="editForm.customer_source" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="售后二维码PRO" /><p class="text-xs text-gray-400 mt-1">客户同步时的来源标识</p></div>
        <!-- 允许重复订阅 -->
        <div class="flex items-center justify-between py-2 border border-gray-200 rounded-lg px-3">
          <div>
            <p class="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5.5">允许重复订阅<ConfigHelpButton help-key="allow_duplicate_subscribe" size="md" /></p>
            <p class="text-xs text-gray-400 mt-0.5">{{ editForm.allow_duplicate_subscribe ? '同一邮箱可多次提交订阅' : '同一邮箱只能订阅一次' }}</p>
          </div>
          <button @click="editForm.allow_duplicate_subscribe = !editForm.allow_duplicate_subscribe"
            :class="editForm.allow_duplicate_subscribe ? 'bg-blue-600' : 'bg-gray-300'"
            class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none">
            <span :class="editForm.allow_duplicate_subscribe ? 'translate-x-4' : 'translate-x-0.5'"
              class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span>
          </button>
        </div>

        <!-- 验证通过后自动跳转第二页 -->
        <div class="flex items-center justify-between py-2 border border-gray-200 rounded-lg px-3">
          <div>
            <p class="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5.5">自动跳转第二页<ConfigHelpButton help-key="auto_redirect_page2" size="md" /></p>
            <p class="text-xs text-gray-400 mt-0.5">{{ editForm.auto_redirect_page2 ? '后台扫到验证码后，买家打开的第一页自动跳转第二页' : '买家需自行点击邮件中的激活链接进入第二页' }}</p>
          </div>
          <button @click="editForm.auto_redirect_page2 = !editForm.auto_redirect_page2"
            :class="editForm.auto_redirect_page2 ? 'bg-blue-600' : 'bg-gray-300'"
            class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none">
            <span :class="editForm.auto_redirect_page2 ? 'translate-x-4' : 'translate-x-0.5'"
              class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span>
          </button>
        </div>

        <!-- 自动标签 -->
        <div>
          <ConfigHelpLabel label="自动标签" help-key="auto_tags" label-class="block text-sm font-medium text-gray-700 mb-2" />
          <div class="flex flex-wrap gap-1 mb-2">
            <span v-for="tag in (editForm.auto_tags_display || [])" :key="tag.id" class="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs">
              {{ tag.name }}
              <button @click="removeAutoTag(tag.id)" class="text-blue-400 hover:text-blue-700 leading-none">&times;</button>
            </span>
          </div>
          <div class="relative">
            <input v-model="autoTagInput" @keydown="onTagKeydown" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="输入标签名，回车添加" />
            <div v-if="tagSuggestions.length" class="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
              <div v-for="t in tagSuggestions" :key="t.id" @click="addAutoTag(t.name)" class="px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">{{ t.name }}</div>
            </div>
          </div>
          <p class="text-xs text-gray-400 mt-1">订阅提交时自动打上这些标签</p>
        </div>
        <!-- 自定义域名 -->
        <div class="border-t pt-3">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-medium text-gray-900 inline-flex items-center gap-1.5.5">自定义域名配置<ConfigHelpButton help-key="custom_domain" size="md" /></h4>
            <div class="flex gap-2">
              <button v-if="!editForm.custom_domain" @click="openDomainModal" class="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100">+ 绑定域名</button>
              <button v-if="editForm.custom_domain" @click="clearDomain" class="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100">清空域名</button>
            </div>
          </div>
          <div v-if="editForm.custom_domain" class="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
            <div class="text-sm font-medium text-gray-900 mb-1">{{ editForm.custom_domain }}</div>
            <div class="flex items-center gap-3 text-xs">
              <span :class="editForm.domain_verified ? 'text-green-600 font-medium' : 'text-orange-600'">{{ editForm.domain_verified ? '✓ 已验证' : '○ 未验证' }}</span>
              <span :class="editForm.ssl_enabled ? 'text-green-600 font-medium' : 'text-gray-500'">{{ editForm.ssl_enabled ? '🔒 SSL已启用' : '🔓 待配置SSL' }}</span>
            </div>
            <div class="mt-2 flex gap-2">
              <button v-if="!editForm.domain_verified" @click="openDomainModal" class="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700">验证域名</button>
              <button v-else-if="!editForm.ssl_enabled" @click="openDomainModal" class="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700">配置SSL</button>
            </div>
          </div>
          <p v-else class="text-xs text-gray-500">绑定自定义域名，让用户通过您的品牌域名访问页面</p>
        </div>
        </div><!-- /基本信息 tab -->

        <!-- 风控管理 tab -->
        <div v-show="activeTab==='risk'" class="flex-1 overflow-y-auto p-3 space-y-3">
          <!-- 是否检测中国（总开关） -->
          <div class="flex items-center justify-between py-2 border border-gray-200 rounded-lg px-3">
            <div>
              <p class="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5">是否检测中国<ConfigHelpButton help-key="china_detect_enabled" /></p>
            </div>
            <button @click="editForm.china_detect_enabled = !editForm.china_detect_enabled"
              :class="editForm.china_detect_enabled ? 'bg-blue-600' : 'bg-gray-300'"
              class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none">
              <span :class="editForm.china_detect_enabled ? 'translate-x-4' : 'translate-x-0.5'"
                class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span>
            </button>
          </div>

          <!-- 检测方法（总开关关闭时置灰） -->
          <div class="border border-gray-200 rounded-lg px-3 py-2 space-y-2" :class="editForm.china_detect_enabled ? '' : 'opacity-50 pointer-events-none'">
            <p class="text-xs font-semibold text-gray-500">检测方法（满足任一即判定为中国）</p>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">浏览器语言 (zh)<ConfigHelpButton help-key="china_detect_by_language" /></span>
              <button @click="editForm.china_detect_by_language = !editForm.china_detect_by_language" :class="editForm.china_detect_by_language ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_language ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">IP 国家 (CN)<ConfigHelpButton help-key="china_detect_by_ip" /></span>
              <button @click="editForm.china_detect_by_ip = !editForm.china_detect_by_ip" :class="editForm.china_detect_by_ip ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_ip ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">时区 (Asia/Shanghai)<ConfigHelpButton help-key="china_detect_by_timezone" /></span>
              <button @click="editForm.china_detect_by_timezone = !editForm.china_detect_by_timezone" :class="editForm.china_detect_by_timezone ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_timezone ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">国产浏览器 <span class="text-gray-400 text-xs">(微信/QQ/UC/夸克等)</span><ConfigHelpButton help-key="china_detect_by_ua" /></span>
              <button @click="editForm.china_detect_by_ua = !editForm.china_detect_by_ua" :class="editForm.china_detect_by_ua ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_ua ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">时区偏移 <span class="text-gray-400 text-xs">(UTC+8)</span><ConfigHelpButton help-key="china_detect_by_utc_offset" /></span>
              <button @click="editForm.china_detect_by_utc_offset = !editForm.china_detect_by_utc_offset" :class="editForm.china_detect_by_utc_offset ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_utc_offset ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">语言列表含 zh<ConfigHelpButton help-key="china_detect_by_nav_languages" /></span>
              <button @click="editForm.china_detect_by_nav_languages = !editForm.china_detect_by_nav_languages" :class="editForm.china_detect_by_nav_languages ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_nav_languages ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-700 inline-flex items-center gap-1.5">中文字体 <span class="text-gray-400 text-xs">(宋体/雅黑等)</span><ConfigHelpButton help-key="china_detect_by_fonts" /></span>
              <button @click="editForm.china_detect_by_fonts = !editForm.china_detect_by_fonts" :class="editForm.china_detect_by_fonts ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_fonts ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <div><span class="text-sm text-gray-700 inline-flex items-center gap-1.5">网络连通性探测<ConfigHelpButton help-key="china_detect_by_connectivity" /></span><p class="text-[10px] text-gray-400">探测墙外资源，最强但有延迟/可能误伤防火墙用户</p></div>
              <button @click="editForm.china_detect_by_connectivity = !editForm.china_detect_by_connectivity" :class="editForm.china_detect_by_connectivity ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_detect_by_connectivity ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
          </div>

          <!-- 检测到中国后 -->
          <div class="border border-gray-200 rounded-lg px-3 py-2 space-y-2" :class="editForm.china_detect_enabled ? '' : 'opacity-50 pointer-events-none'">
            <p class="text-xs font-semibold text-gray-500">检测到中国后</p>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-700 inline-flex items-center gap-1.5">是否发送邮件<ConfigHelpButton help-key="send_email_to_china" /></p>
              </div>
              <button @click="editForm.send_email_to_china = !editForm.send_email_to_china" :class="editForm.send_email_to_china ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.send_email_to_china ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div v-show="editForm.send_email_to_china" class="space-y-2">
              <div><ConfigHelpLabel label="单独邮件主题" help-key="china_email_subject" label-class="block text-xs text-gray-500 mb-1" /><input v-model="editForm.china_email_subject" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="留空则使用建联成功邮件" /></div>
              <div><ConfigHelpLabel label="单独邮件正文" help-key="china_email_body" label-class="block text-xs text-gray-500 mb-1" /><textarea v-model="editForm.china_email_body" rows="4" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="支持 {name} 占位；留空则使用建联成功邮件"></textarea></div>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-700 inline-flex items-center gap-1.5">是否入库<ConfigHelpButton help-key="china_ingest" /></p>
              </div>
              <button @click="editForm.china_ingest = !editForm.china_ingest" :class="editForm.china_ingest ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_ingest ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-700 inline-flex items-center gap-1.5">是否展示中国专属 HTML<ConfigHelpButton help-key="china_html_enabled" /></p>
              </div>
              <button @click="editForm.china_html_enabled = !editForm.china_html_enabled" :class="editForm.china_html_enabled ? 'bg-blue-600' : 'bg-gray-300'" class="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors focus:outline-none"><span :class="editForm.china_html_enabled ? 'translate-x-4' : 'translate-x-0.5'" class="inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform"></span></button>
            </div>
            <div v-show="editForm.china_html_enabled">
              <ConfigHelpLabel label="检测到中国后展示的 HTML" help-key="china_html" label-class="block text-xs text-gray-500 mb-1" />
              <textarea v-model="editForm.china_html" rows="6" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono" placeholder="开启开关并填写后，检测为中国的访客将整页显示此 HTML（支持完整 <html> 文档或页面片段）"></textarea>
              <p class="text-[10px] text-gray-400 mt-1">命中中国即整页替换为此 HTML，跳过正常落地页渲染（预览不受影响）。</p>
            </div>
          </div>
        </div><!-- /风控管理 tab -->
      </div>
    </div>

    <!-- 第3栏：批次管理 + 订阅记录 -->
    <div class="w-96 border-r border-gray-200 flex flex-col flex-shrink-0">
      <div class="px-3 h-11 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <h3 class="text-xs font-semibold text-gray-500 uppercase">批次与订阅</h3>
        <div class="flex items-center gap-2">
          <button v-if="currentPage" @click="showSubsModal = true" class="text-xs text-gray-500 hover:text-blue-600">订阅({{ subscriptions.length }})</button>
          <button v-if="currentPage" @click="showBatchModal = true" class="text-xs text-blue-600 hover:text-blue-800">+ 新建</button>
        </div>
      </div>
      <div v-if="!currentPage" class="flex-1 flex items-center justify-center text-gray-400 text-sm">选择页面</div>
      <div v-else class="flex-1 overflow-y-auto">
        <!-- 批次列表 -->
        <div class="divide-y divide-gray-100">
          <div v-if="!currentPage.batches || !currentPage.batches.length" class="px-4 py-3 text-xs text-gray-400">暂无批次，点击右上角新建</div>
          <div v-for="batch in currentPage.batches" :key="batch.id" class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors relative" :class="{ 'bg-blue-50': currentBatch?.id === batch.id, 'opacity-50 pointer-events-none': loadingBatchId && loadingBatchId !== batch.id }" @click="selectBatch(batch)">
            <div v-if="loadingBatchId === batch.id" class="absolute inset-0 bg-blue-50 bg-opacity-75 flex items-center justify-center z-10">
              <div class="animate-spin h-5 w-5 text-blue-600">
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-gray-900 truncate">{{ batch.name }}</span>
              <div class="flex items-center gap-1.5 flex-shrink-0 ml-2">
                <span v-if="hasBatchContent(batch)" class="inline-block w-1.5 h-1.5 rounded-full bg-green-500" title="已自定义内容"></span>
                <span class="text-xs text-gray-400">发货 {{ batch.shipment_quantity }}</span>
                <span class="text-xs text-gray-400">· <span class="font-semibold text-blue-600">{{ batch.scan_count }}</span> 扫码</span>
                <span v-if="batch.created_by_email" class="text-[10px] text-gray-400 truncate max-w-[90px]" :title="'创建者: ' + batch.created_by_email">{{ batch.created_by_email }}</span>
              </div>
            </div>
            <p v-if="batch.notes" class="text-xs text-gray-500 mb-2 whitespace-pre-wrap break-words" :title="batch.notes">备注：{{ batch.notes }}</p>
            <div v-if="batch.qr_code_url" class="mt-2">
              <img :src="batch.qr_code_url" alt="QR" class="w-20 h-20 border border-gray-200 rounded cursor-pointer hover:opacity-80" @click.stop="openQrZoom(batch)" />
              <div class="flex items-center gap-2 mt-1.5">
                <button @click.stop="downloadQR(batch)" class="text-[11px] text-blue-600 hover:text-blue-800">下载</button>
                <span class="text-gray-300">|</span>
                <button @click.stop="copyLink(batch)" class="text-[11px] text-blue-600 hover:text-blue-800">复制链接</button>
                <span class="text-gray-300">|</span>
                <button @click.stop="openEditBatch(batch)" class="text-[11px] text-blue-600 hover:text-blue-800">编辑批次</button>
                <span class="text-gray-300">|</span>
                <button @click.stop="duplicateBatch(batch)" class="text-[11px] text-blue-600 hover:text-blue-800">复制批次</button>
                <template v-if="batch.can_delete !== false">
                  <span class="text-gray-300">|</span>
                  <button @click.stop="deleteBatch(batch)" class="text-[11px] text-red-500 hover:text-red-700">删除</button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 第4栏：落地页预览 -->
    <div class="flex-1 border-r border-gray-200 flex flex-col min-w-0">
      <div class="px-3 h-11 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div class="flex items-center gap-1">
          <button @click="previewPage = 1" :class="previewPage === 1 ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'" class="px-2 py-0.5 rounded text-[10px] font-medium transition-colors">第一页</button>
          <button @click="previewPage = 2" :class="previewPage === 2 ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'" class="px-2 py-0.5 rounded text-[10px] font-medium transition-colors">第二页</button>
        </div>
        <button v-if="currentPage && previewIframeSrc" @click="refreshPreview" class="text-gray-400 hover:text-gray-600" title="刷新预览">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h5M20 20v-5h-5M5 19a9 9 0 0114.3-7.3M19 5a9 9 0 01-14.3 7.3" /></svg>
        </button>
      </div>
      <div v-if="!currentPage" class="flex-1 flex items-center justify-center text-gray-400 text-sm">选择页面</div>
      <div v-else-if="!previewIframeSrc" class="flex-1 flex items-center justify-center text-gray-400 text-sm">
        <div class="text-center">
          <p class="text-sm">{{ previewPage === 1 ? '请先创建批次以预览第一页' : '需要已验证的订阅才能预览第二页' }}</p>
        </div>
      </div>
      <div v-else class="flex-1 overflow-hidden bg-gray-100">
        <iframe :key="previewKey" :src="previewIframeSrc" class="w-full h-full border-0" style="transform-origin:top left" />
      </div>
    </div>

    <!-- 第5栏：编辑落地页内容 -->
    <div class="w-96 flex flex-col flex-shrink-0">
      <div class="px-3 h-11 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <div v-if="currentPage" class="flex items-center gap-1">
          <button @click="contentEditMode = 'blocks'" :class="contentEditMode === 'blocks' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'" class="px-2 py-0.5 rounded text-[10px] font-medium transition-colors">页面模块</button>
          <button @click="contentEditMode = 'other'" :class="contentEditMode === 'other' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'" class="px-2 py-0.5 rounded text-[10px] font-medium transition-colors">其他配置</button>
        </div>
        <h3 v-else class="text-xs font-semibold text-gray-500 uppercase">显示内容</h3>
        <select v-if="currentPage" v-model="editLang" class="w-28 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"><option value="en">English</option><option value="de">Deutsch</option><option value="fr">Français</option><option value="it">Italiano</option><option value="es">Español</option><option value="ja">日本語</option><option value="zh">中文</option></select>
      </div>
      <div v-if="!currentPage" class="flex-1 flex items-center justify-center text-gray-400 text-sm">选择页面后编辑</div>
      <!-- 页面模块构建器（批次级） -->
      <div v-else-if="contentEditMode === 'blocks'" class="flex-1 flex flex-col overflow-hidden">
        <div v-if="!currentBatch" class="flex-1 flex items-center justify-center text-gray-400 text-sm">请在左侧选择一个批次</div>
        <template v-else>
          <div class="px-3 py-2 bg-blue-50 border-b border-blue-100 flex-shrink-0 flex items-center justify-between">
            <p class="text-xs text-blue-700 font-medium">正在编辑：{{ currentBatch.name }}</p>
            <span class="text-[10px] px-1.5 py-0.5 rounded" :class="previewPage === 2 ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'">{{ previewPage === 2 ? '第二页' : '第一页' }}</span>
          </div>
          <BlockBuilder v-if="previewPage === 2" v-model="batchContentCurrent.page2_blocks" :page="2" :lang-content="batchContentCurrent" :page-id="currentPage?.id" @update:modelValue="onPage2BlocksUpdate" />
          <BlockBuilder v-else v-model="batchContentCurrent.blocks" :page="1" :lang-content="batchContentCurrent" :page-id="currentPage?.id" @update:modelValue="onBlocksUpdate" />
        </template>
      </div>
      <!-- 其他配置（模块中没有的字段） -->
      <div v-else-if="contentEditMode === 'other'" class="flex-1 overflow-y-auto p-4 space-y-3 pb-16">
        <OtherConfigPanel v-if="currentBatch" :batch-content="batchContentCurrent" :page-default="pageDefault" @add-faq="addBatchFaqItem" @remove-faq="removeBatchFaqItem" />
        <div v-else class="flex items-center justify-center h-full text-gray-400 text-sm">请在左侧选择一个批次</div>
      </div>
      <div class="p-3 border-t border-gray-200 bg-white">
        <button @click="saveBatchContent" :disabled="!currentBatch" class="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">保存</button>
      </div>
    </div>
    </div>

    <!-- 创建页面弹窗 -->
    <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/30" @click="showCreateModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">新建PRO页面</h2>
        <div class="space-y-4">
          <div><label class="block text-sm font-medium text-gray-700 mb-2">页面名称 *</label><input v-model="newPage.name" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="如：产品A售后页面" /></div>
        </div>
        <div class="flex justify-end gap-3 mt-5">
          <button @click="showCreateModal = false" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">取消</button>
          <button @click="createPage" :disabled="!newPage.name" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">创建</button>
        </div>
      </div>
    </div>
    <!-- 创建批次弹窗 -->
    <div v-if="showBatchModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/30" @click="showBatchModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">新建批次</h2>
        <div class="space-y-4">
          <div><ConfigHelpLabel label="批次名称 *" help-key="batch_name" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="newBatch.name" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="如：第一批-2024年6月" /></div>
          <div><ConfigHelpLabel label="发货数量" help-key="batch_shipment_quantity" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model.number="newBatch.shipment_quantity" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" /></div>
          <div><ConfigHelpLabel label="备注" help-key="batch_notes" label-class="block text-sm font-medium text-gray-700 mb-2" /><textarea v-model="newBatch.notes" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" rows="2"></textarea></div>
        </div>
        <div class="flex justify-end gap-3 mt-5">
          <button @click="showBatchModal = false" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">取消</button>
          <button @click="createBatch" :disabled="!newBatch.name" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">创建</button>
        </div>
      </div>
    </div>
    <!-- 编辑批次弹窗 -->
    <div v-if="showEditBatchModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/30" @click="showEditBatchModal = false"></div>
      <div class="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">编辑批次</h2>
        <div class="space-y-4">
          <div><ConfigHelpLabel label="批次名称 *" help-key="batch_name" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model="editBatchForm.name" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" /></div>
          <div><ConfigHelpLabel label="发货数量" help-key="batch_shipment_quantity" label-class="block text-sm font-medium text-gray-700 mb-2" /><input v-model.number="editBatchForm.shipment_quantity" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" /></div>
          <div><ConfigHelpLabel label="备注" help-key="batch_notes" label-class="block text-sm font-medium text-gray-700 mb-2" /><textarea v-model="editBatchForm.notes" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" rows="2"></textarea></div>
        </div>
        <div class="flex justify-end gap-3 mt-5">
          <button @click="showEditBatchModal = false" class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">取消</button>
          <button @click="saveEditBatch" :disabled="!editBatchForm.name" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">保存</button>
        </div>
      </div>
    </div>
    <!-- 域名配置模态框 -->
    <Teleport to="body">
      <div v-if="showDomainModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeDomainModal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeDomainModal"></div>
          <div class="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full" @click.stop>
            <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">自定义域名配置</h3>
              <button @click="closeDomainModal" class="text-gray-400 hover:text-gray-600"><svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
            </div>
            <!-- 步骤指示器 -->
            <div class="px-6 py-4 bg-gray-50 flex items-center justify-between">
              <div class="flex items-center flex-1 min-w-0">
                <div :class="['w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold', modalStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500']">{{ domainStatus.verified && modalStep > 1 ? '✓' : '1' }}</div>
                <div class="ml-3 text-sm"><div :class="['font-medium', modalStep >= 1 ? 'text-gray-900' : 'text-gray-500']">配置DNS</div><div class="text-xs text-gray-500">设置CNAME记录</div></div>
              </div>
              <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 1 ? 'bg-blue-600' : 'bg-gray-200']"></div>
              <div class="flex items-center flex-1 min-w-0">
                <div :class="['w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold', modalStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500']">{{ domainStatus.verified && modalStep > 2 ? '✓' : '2' }}</div>
                <div class="ml-3 text-sm"><div :class="['font-medium', modalStep >= 2 ? 'text-gray-900' : 'text-gray-500']">验证域名</div><div class="text-xs text-gray-500">检查DNS解析</div></div>
              </div>
              <div :class="['flex-shrink-0 w-12 h-0.5 mx-2', modalStep > 2 ? 'bg-blue-600' : 'bg-gray-200']"></div>
              <div class="flex items-center flex-1 min-w-0">
                <div :class="['w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-semibold', modalStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500']">{{ domainStatus.ssl_enabled ? '✓' : '3' }}</div>
                <div class="ml-3 text-sm"><div :class="['font-medium', modalStep >= 3 ? 'text-gray-900' : 'text-gray-500']">启用SSL</div><div class="text-xs text-gray-500">申请HTTPS证书</div></div>
              </div>
            </div>
            <!-- 主体 -->
            <div class="px-6 py-6">
              <!-- 步骤1 -->
              <div v-if="modalStep === 1" class="space-y-4">
                <div><ConfigHelpLabel help-key="domain_address" label-class="block text-sm font-medium text-gray-700 mb-2">域名地址 <span class="text-red-500">*</span></ConfigHelpLabel><input v-model="modalDomain" type="text" class="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="例如：review.yourbrand.com" /><p class="text-xs text-gray-500 mt-1">请输入您要绑定的完整域名</p></div>
                <div class="border border-gray-200 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-900 mb-3">DNS配置说明</h5>
                  <div class="space-y-2 text-sm text-gray-700">
                    <p><span class="font-medium">1.</span> 登录您的域名DNS服务商管理后台</p>
                    <p><span class="font-medium">2.</span> 添加一条CNAME记录：</p>
                    <div class="bg-gray-50 rounded-lg p-3 font-mono text-xs space-y-1.5 border border-gray-200">
                      <div class="flex justify-between"><span class="text-gray-500">类型：</span><span class="font-semibold">CNAME</span></div>
                      <div class="flex justify-between"><span class="text-gray-500">主机记录：</span><span class="font-semibold">{{ getHostRecord(modalDomain) }}</span></div>
                      <div class="flex justify-between"><span class="text-gray-500">记录值：</span><span class="font-semibold text-blue-600">apis.bubu.im</span></div>
                      <div class="flex justify-between"><span class="text-gray-500">TTL：</span><span class="font-semibold">600（或默认值）</span></div>
                    </div>
                    <p><span class="font-medium">3.</span> 等待DNS解析生效（通常5-30分钟）</p>
                    <p><span class="font-medium">4.</span> 点击"下一步"进行验证</p>
                  </div>
                </div>
              </div>
              <!-- 步骤2 -->
              <div v-if="modalStep === 2" class="space-y-4">
                <div class="py-4">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">验证域名解析</h4>
                  <div class="border border-gray-200 rounded-lg p-4 mb-4 text-sm space-y-2">
                    <div class="flex justify-between"><span class="text-gray-600">域名：</span><span class="font-semibold">{{ modalDomain || editForm.custom_domain }}</span></div>
                    <div class="flex justify-between pt-2 border-t border-gray-100"><span class="text-gray-600">状态：</span><span :class="domainStatus.verified ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'">{{ domainStatus.verified ? '已验证' : '待验证' }}</span></div>
                  </div>
                  <button @click="verifyDomainInModal" :disabled="isVerifyingDomain" class="w-full px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
                    <svg v-if="isVerifyingDomain" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    {{ isVerifyingDomain ? '验证中' : '验证域名' }}
                  </button>
                  <p class="mt-3 text-xs text-gray-500">如果验证失败，请检查DNS配置是否正确，并等待解析生效</p>
                </div>
              </div>
              <!-- 步骤3 -->
              <div v-if="modalStep === 3" class="space-y-4">
                <div class="py-4">
                  <h4 class="text-base font-semibold text-gray-900 mb-2">配置SSL证书</h4>
                  <div class="border border-gray-200 rounded-lg p-4 mb-4 text-sm space-y-2">
                    <div class="flex justify-between"><span class="text-gray-600">域名：</span><span class="font-semibold">{{ editForm.custom_domain }}</span></div>
                    <div class="flex justify-between pt-2 border-t border-gray-100"><span class="text-gray-600">SSL状态：</span><span :class="domainStatus.ssl_enabled ? 'text-green-600 font-semibold' : 'text-gray-500 font-semibold'">{{ domainStatus.ssl_enabled ? '已启用' : '待配置' }}</span></div>
                  </div>
                  <button v-if="!domainStatus.ssl_enabled" @click="applySSLInModal" :disabled="isSettingUpSSL" class="w-full px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center">
                    <svg v-if="isSettingUpSSL" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    {{ isSettingUpSSL ? '申请中' : '启用SSL' }}
                  </button>
                  <div v-else class="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">✓ SSL证书已成功配置，您的域名现已支持HTTPS安全访问</div>
                  <p v-if="!domainStatus.ssl_enabled" class="mt-3 text-xs text-gray-500">SSL证书申请需要1-2分钟，请耐心等待</p>
                </div>
              </div>
            </div>
            <!-- 底部 -->
            <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex justify-between">
              <button v-if="modalStep > 1 && !domainStatus.ssl_enabled" @click="modalStep--" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">上一步</button>
              <div v-else></div>
              <div class="flex gap-2">
                <button @click="closeDomainModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">{{ domainStatus.ssl_enabled && modalStep === 3 ? '完成' : '取消' }}</button>
                <button v-if="modalStep === 1" @click="saveAndNextStep" :disabled="!modalDomain || isSavingDomain" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center">
                  <svg v-if="isSavingDomain" class="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  {{ isSavingDomain ? '保存中' : '下一步' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 二维码放大弹窗 -->
    <Teleport to="body">
      <div v-if="qrZoom.show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/60" @click.self="qrZoom.show=false">
        <div class="bg-white rounded-xl p-6 flex flex-col items-center gap-4 shadow-2xl">
          <div class="flex items-center justify-between w-full">
            <span class="text-sm font-semibold text-gray-800">{{ qrZoom.name }}</span>
            <button @click="qrZoom.show=false" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>
          <img :src="qrZoom.url" alt="QR" class="w-64 h-64 border border-gray-200 rounded" />
          <div class="flex gap-3">
            <button @click="downloadQR(qrZoom)" class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">下载二维码</button>
            <button @click="copyLink(qrZoom)" class="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">复制链接</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 最近订阅模态框 -->
    <Teleport to="body">
      <div v-if="showSubsModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="showSubsModal=false">
        <div class="bg-white rounded-xl shadow-2xl w-[560px] max-h-[80vh] flex flex-col">
          <div class="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-base font-semibold text-gray-900">最近订阅</h3>
            <button @click="showSubsModal=false" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>
          <div class="px-4 py-3 border-b border-gray-100">
            <input v-model="subsSearch" type="text" placeholder="搜索姓名、邮箱、批次..." class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div class="flex-1 overflow-y-auto divide-y divide-gray-100">
            <div v-if="!filteredSubs.length" class="px-5 py-6 text-sm text-gray-400 text-center">暂无订阅记录</div>
            <div v-for="sub in filteredSubs" :key="sub.id" class="px-5 py-3 flex items-center justify-between">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-900">{{ sub.name }}</span>
                  <span v-if="sub.batch_name" class="text-[11px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{{ sub.batch_name }}</span>
                </div>
                <div class="text-xs text-gray-400 mt-0.5">{{ sub.email }}</div>
              </div>
              <span class="flex-shrink-0 ml-3 text-[11px] px-2 py-0.5 rounded-full"
                :class="{
                  'bg-yellow-50 text-yellow-700': sub.status==='pending',
                  'bg-green-50 text-green-700': sub.status==='verified',
                  'bg-blue-50 text-blue-700': sub.status==='rated',
                  'bg-purple-50 text-purple-700': sub.status==='reviewed'
                }">{{ sub.status_display }}</span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { QrCodeIcon, PlusIcon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import { qrCodeProService } from '@/services/qrCodeProService'
import { apiRequest } from '@/utils/api'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import BlockBuilder from '@/components/qr-pro/BlockBuilder.vue'
import OtherConfigPanel from '@/components/qr-pro/OtherConfigPanel.vue'
import ConfigHelpLabel from '@/components/qr-pro/ConfigHelpLabel.vue'
import ConfigHelpButton from '@/components/qr-pro/ConfigHelpButton.vue'

const toast = useToast()
const loading = ref(true)
const loadingPageId = ref(null)
const loadingBatchId = ref(null)
const allPages = ref([])
const currentPage = ref(null)
const subscriptions = ref([])
const searchQuery = ref('')
const listPage = ref(1)
const listTotalCount = ref(0)
const listTotalPages = ref(1)
const listPageSize = 20
const showCreateModal = ref(false)
const showBatchModal = ref(false)
const showEditBatchModal = ref(false)
const editBatchForm = ref({ id: null, name: '', shipment_quantity: 0, notes: '' })
const previewPage = ref(1)
const previewKey = ref(Date.now())
const contentEditMode = ref('blocks')
const currentBatch = ref(null)
const batchContentCurrent = ref({})
const showDomainModal = ref(false)
const showSubsModal = ref(false)
const subsSearch = ref('')
const modalStep = ref(1)
const modalDomain = ref('')
const isSavingDomain = ref(false)
const isVerifyingDomain = ref(false)
const isSettingUpSSL = ref(false)
const domainStatus = ref({ verified: false, ssl_enabled: false, full_url: '' })
const editLang = ref('en')
const emailAccounts = ref([])
const customerGroups = ref([])
const newPage = ref({ name: '' })
const newBatch = ref({ name: '', shipment_quantity: 0, notes: '' })
const editForm = ref({})
const activeTab = ref('basic')  // 页面信息栏 tab：basic 基本信息 / risk 风控管理

const emailAccountOptions = computed(() => emailAccounts.value)
const customerGroupOptions = computed(() => customerGroups.value)

const filteredSubs = computed(() => {
  const q = subsSearch.value.toLowerCase()
  if (!q) return subscriptions.value
  return subscriptions.value.filter(s =>
    (s.name || '').toLowerCase().includes(q) ||
    (s.email || '').toLowerCase().includes(q) ||
    (s.batch_name || '').toLowerCase().includes(q)
  )
})



const previewIframeSrc = computed(() => {
  if (!currentPage.value) return ''
  const langParam = `lang=${editLang.value}&_t=${previewKey.value}&preview=1`
  if (previewPage.value === 1) {
    const batches = currentPage.value.batches || []
    const batch = currentBatch.value || batches[0]
    if (!batch) return ''
    return `https://apis.bubu.im/qr-pro/page/${currentPage.value.uuid}/${batch.uuid}/?${langParam}`
  } else {
    // 第二页预览：优先使用 page+batch 预览路由，没有批次则用 default
    const batches = currentPage.value.batches || []
    const batch = currentBatch.value || batches[0]
    const batchUuid = batch ? batch.uuid : 'default'
    return `https://apis.bubu.im/qr-pro/activate-preview/${currentPage.value.uuid}/${batchUuid}/?${langParam}`
  }
})

const refreshPreview = () => { previewKey.value = Date.now() }

const currentLangContent = ref({})

const defaultContent = {
  en: { page_title: 'Product Warranty Registration', activate_page_title: 'Activation Successful', title: 'Thank you for your purchase!', description: 'Register your product warranty', subscribe_label: 'I agree to subscribe', confirm_button: 'Confirm', email_button: 'Email Us', success_title: '✅ Submitted!', success_message: 'Please check your inbox and reply with the verification code.', mail_app_countdown: 'Opening your email app in {n} seconds...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'Your Name', email_placeholder: 'Email Address', duplicate_message: 'This email has already subscribed. Please do not subscribe again.', activate_title: 'Activation Successful', rate_prompt: 'How would you rate our product?', review_prompt: 'Thank you! We\'d love your review.', review_button: 'Leave a Review', low_rate_prompt: 'Thank you for your feedback.', countdown_text: 'Redirecting in {n}s...', verification_subject: 'Please verify your subscription - Code: {{code}}', verification_body: '<p>Hi {{name}},</p><p>Please reply with the code <strong>{{code}}</strong> to complete your subscription.</p>', success_subject: 'Subscription Confirmed!', success_body: '<p>Hi {{name}},</p><p>Your subscription is confirmed! <a href="{{activate_url}}">Click here to activate</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  zh: { page_title: '产品保修登记', activate_page_title: '激活成功', title: '感谢您的购买！', description: '注册您的产品保修', subscribe_label: '我同意订阅产品更新', confirm_button: '确认', email_button: '邮件联系我们', success_title: '✅ 提交成功！', success_message: '请检查收件箱或垃圾箱，回复验证码完成订阅。', mail_app_countdown: '{n}秒后将自动打开邮箱App...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: '您的姓名', email_placeholder: '邮箱地址', duplicate_message: '该邮箱已订阅，请不要重复订阅。', activate_title: '激活成功', rate_prompt: '您对我们的产品评价如何？', review_prompt: '感谢您！期待您的评价。', review_button: '去留评', low_rate_prompt: '感谢您的反馈。', countdown_text: '{n}秒后自动跳转...', verification_subject: '请回复验证码 {{code}} 完成订阅', verification_body: '<p>您好 {{name}}，</p><p>请回复验证码 <strong>{{code}}</strong> 完成订阅。</p>', success_subject: '订阅成功！', success_body: '<p>您好 {{name}}，</p><p>您的订阅已确认！<a href="{{activate_url}}">点击此处激活</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  de: { page_title: 'Produktgarantie-Registrierung', activate_page_title: 'Aktivierung erfolgreich', title: 'Vielen Dank für Ihren Kauf!', description: 'Registrieren Sie Ihre Produktgarantie', subscribe_label: 'Ich stimme dem Abonnement zu', confirm_button: 'Bestätigen', email_button: 'E-Mail an uns', success_title: '✅ Eingereicht!', success_message: 'Bitte überprüfen Sie Ihren Posteingang und antworten Sie mit dem Code.', mail_app_countdown: 'Ihre E-Mail-App wird in {n} Sekunden geöffnet...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'Ihr Name', email_placeholder: 'E-Mail-Adresse', duplicate_message: 'Diese E-Mail ist bereits abonniert. Bitte nicht erneut abonnieren.', activate_title: 'Aktivierung erfolgreich', rate_prompt: 'Wie würden Sie unser Produkt bewerten?', review_prompt: 'Danke! Wir freuen uns über Ihre Bewertung.', review_button: 'Bewertung schreiben', low_rate_prompt: 'Danke für Ihr Feedback.', countdown_text: 'Weiterleitung in {n}s...', verification_subject: 'Bitte bestätigen Sie Ihr Abonnement - Code: {{code}}', verification_body: '<p>Hallo {{name}},</p><p>Bitte antworten Sie mit dem Code <strong>{{code}}</strong>.</p>', success_subject: 'Abonnement bestätigt!', success_body: '<p>Hallo {{name}},</p><p>Ihr Abonnement wurde bestätigt! <a href="{{activate_url}}">Hier klicken zum Aktivieren</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  fr: { page_title: 'Enregistrement de garantie produit', activate_page_title: 'Activation réussie', title: 'Merci pour votre achat !', description: 'Enregistrez la garantie de votre produit', subscribe_label: 'J\'accepte de m\'abonner', confirm_button: 'Confirmer', email_button: 'Envoyez-nous un e-mail', success_title: '✅ Envoyé !', success_message: 'Veuillez vérifier votre boîte de réception et répondre avec le code de vérification.', mail_app_countdown: 'Ouverture de votre messagerie dans {n} secondes...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'Votre nom', email_placeholder: 'Adresse e-mail', duplicate_message: 'Cet e-mail est déjà abonné. Veuillez ne pas vous réabonner.', activate_title: 'Activation réussie', rate_prompt: 'Comment évaluez-vous notre produit ?', review_prompt: 'Merci ! Nous aimerions votre avis.', review_button: 'Laisser un avis', low_rate_prompt: 'Merci pour vos commentaires.', countdown_text: 'Redirection dans {n}s...', verification_subject: 'Veuillez vérifier votre abonnement - Code : {{code}}', verification_body: '<p>Bonjour {{name}},</p><p>Veuillez répondre avec le code <strong>{{code}}</strong> pour finaliser votre abonnement.</p>', success_subject: 'Abonnement confirmé !', success_body: '<p>Bonjour {{name}},</p><p>Votre abonnement est confirmé ! <a href="{{activate_url}}">Cliquez ici pour activer</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  it: { page_title: 'Registrazione garanzia prodotto', activate_page_title: 'Attivazione riuscita', title: 'Grazie per il tuo acquisto!', description: 'Registra la garanzia del tuo prodotto', subscribe_label: 'Accetto di iscrivermi', confirm_button: 'Conferma', email_button: 'Scrivici un\'email', success_title: '✅ Inviato!', success_message: 'Controlla la tua casella di posta e rispondi con il codice di verifica.', mail_app_countdown: 'Apertura della tua app email tra {n} secondi...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'Il tuo nome', email_placeholder: 'Indirizzo e-mail', duplicate_message: 'Questa e-mail è già iscritta. Non iscriverti di nuovo.', activate_title: 'Attivazione riuscita', rate_prompt: 'Come valuti il nostro prodotto?', review_prompt: 'Grazie! Ci piacerebbe ricevere la tua recensione.', review_button: 'Lascia una recensione', low_rate_prompt: 'Grazie per il tuo feedback.', countdown_text: 'Reindirizzamento in {n}s...', verification_subject: 'Verifica il tuo abbonamento - Codice: {{code}}', verification_body: '<p>Ciao {{name}},</p><p>Rispondi con il codice <strong>{{code}}</strong> per completare l\'iscrizione.</p>', success_subject: 'Abbonamento confermato!', success_body: '<p>Ciao {{name}},</p><p>Il tuo abbonamento è confermato! <a href="{{activate_url}}">Clicca qui per attivare</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  es: { page_title: 'Registro de garantía del producto', activate_page_title: 'Activación exitosa', title: '¡Gracias por tu compra!', description: 'Registra la garantía de tu producto', subscribe_label: 'Acepto suscribirme', confirm_button: 'Confirmar', email_button: 'Envíanos un correo', success_title: '✅ ¡Enviado!', success_message: 'Por favor, revisa tu bandeja de entrada y responde con el código de verificación.', mail_app_countdown: 'Abriendo tu app de correo en {n} segundos...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'Tu nombre', email_placeholder: 'Dirección de correo', duplicate_message: 'Este correo ya está suscrito. Por favor, no te suscribas de nuevo.', activate_title: 'Activación exitosa', rate_prompt: '¿Cómo calificarías nuestro producto?', review_prompt: '¡Gracias! Nos encantaría tu reseña.', review_button: 'Dejar una reseña', low_rate_prompt: 'Gracias por tus comentarios.', countdown_text: 'Redirigiendo en {n}s...', verification_subject: 'Por favor verifica tu suscripción - Código: {{code}}', verification_body: '<p>Hola {{name}},</p><p>Responde con el código <strong>{{code}}</strong> para completar tu suscripción.</p>', success_subject: '¡Suscripción confirmada!', success_body: '<p>Hola {{name}},</p><p>¡Tu suscripción está confirmada! <a href="{{activate_url}}">Haz clic aquí para activar</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' },
  ja: { page_title: '製品保証登録', activate_page_title: 'アクティベーション成功', title: 'ご購入ありがとうございます！', description: '製品保証を登録してください', subscribe_label: '購読に同意します', confirm_button: '確認', email_button: 'メールで問い合わせ', success_title: '✅ 送信完了！', success_message: '受信トレイをご確認の上、認証コードを返信してください。', mail_app_countdown: '{n}秒後にメールアプリを開きます...', mail_app_subject: '{{code}}', mail_app_body: '{{code}}', name_placeholder: 'お名前', email_placeholder: 'メールアドレス', duplicate_message: 'このメールはすでに登録されています。重複登録はご遠慮ください。', activate_title: 'アクティベーション成功', rate_prompt: '当社の製品をどう評価しますか？', review_prompt: 'ありがとうございます！レビューをお願いします。', review_button: 'レビューを書く', low_rate_prompt: 'フィードバックありがとうございます。', countdown_text: '{n}秒後にリダイレクトします...', verification_subject: '購読の確認 - コード: {{code}}', verification_body: '<p>{{name}}様、</p><p>コード <strong>{{code}}</strong> を返信して購読を完了してください。</p>', success_subject: '購読が確認されました！', success_body: '<p>{{name}}様、</p><p>購読が確認されました！<a href="{{activate_url}}">こちらをクリックしてアクティベート</a></p>', faq_items: [], youtube_url: '', page2_faq_items: [], page2_youtube_url: '' }
}

const updateLandingContent = () => {
  if (!editForm.value.landing_content) editForm.value.landing_content = {}
  editForm.value.landing_content[editLang.value] = { ...currentLangContent.value }
}

// 初始化 blocks：没有 blocks 时，根据已有字段自动生成默认区块
const ensureBlocks = (content, fallback = null) => {
  if (content.blocks && content.blocks.length) return
  const src = fallback || content
  const blocks = []
  blocks.push({ id: '_heading', type: 'heading', visible: true, margin: 8, data: { text: src.title || '', align: 'center', color: '#111111' } })
  blocks.push({ id: '_text', type: 'text', visible: true, margin: 20, data: { text: src.description || '', align: 'center' } })
  blocks.push({ id: '_form', type: 'form', visible: true, margin: 16, data: { name_placeholder: src.name_placeholder || '', email_placeholder: src.email_placeholder || '', subscribe_label: src.subscribe_label || '', confirm_button: src.confirm_button || '' } })
  blocks.push({ id: '_buttons', type: 'buttons', visible: true, margin: 12, data: { email_button: src.email_button || '' } })
  if (src.youtube_url) blocks.push({ id: '_video', type: 'video', visible: true, margin: 14, data: { youtube_url: src.youtube_url } })
  if (src.faq_items && src.faq_items.length) blocks.push({ id: '_faq', type: 'faq', visible: true, margin: 14, data: { title: 'FAQ', items: JSON.parse(JSON.stringify(src.faq_items)) } })
  content.blocks = blocks
}

const onBlocksUpdate = (newBlocks) => {
  batchContentCurrent.value.blocks = newBlocks
}

// 初始化第二页 blocks：无 page2_blocks 时按 page2_* 字段生成默认 [评星(+视频/FAQ)]
const ensurePage2Blocks = (content, fallback = null) => {
  if (content.page2_blocks && content.page2_blocks.length) return
  const src = fallback || content
  const blocks = []
  blocks.push({ id: '_rating', type: 'rating', visible: true, margin: 14, data: {
    activate_title: src.activate_title || '', rate_prompt: src.rate_prompt || '',
    review_prompt: src.review_prompt || '', review_button: src.review_button || '',
    low_rate_prompt: src.low_rate_prompt || '', countdown_text: src.countdown_text || ''
  } })
  if (src.page2_youtube_url) blocks.push({ id: '_p2video', type: 'video', visible: true, margin: 14, data: { youtube_url: src.page2_youtube_url } })
  if (src.page2_faq_items && src.page2_faq_items.length) blocks.push({ id: '_p2faq', type: 'faq', visible: true, margin: 14, data: { title: 'FAQ', items: JSON.parse(JSON.stringify(src.page2_faq_items)) } })
  content.page2_blocks = blocks
}

const onPage2BlocksUpdate = (newBlocks) => {
  batchContentCurrent.value.page2_blocks = newBlocks
}

const addFaqItem = (field) => {
  if (!currentLangContent.value[field]) currentLangContent.value[field] = []
  currentLangContent.value[field].push({ q: '', a: '' })
  updateLandingContent()
}

const removeFaqItem = (field, idx) => {
  if (currentLangContent.value[field]) {
    currentLangContent.value[field].splice(idx, 1)
    updateLandingContent()
  }
}

const addBatchFaqItem = (field) => {
  if (!batchContentCurrent.value[field]) batchContentCurrent.value[field] = []
  batchContentCurrent.value[field].push({ q: '', a: '' })
}

const removeBatchFaqItem = (field, idx) => {
  if (batchContentCurrent.value[field]) {
    batchContentCurrent.value[field].splice(idx, 1)
  }
}

const loadPages = async (page = listPage.value) => {
  loading.value = true
  const params = { page, page_size: listPageSize }
  if (searchQuery.value.trim()) params.search = searchQuery.value.trim()
  const res = await qrCodeProService.getPages(params)
  if (res.success) {
    allPages.value = res.results || []
    listTotalCount.value = res.count || 0
    listTotalPages.value = res.totalPages || 1
    listPage.value = page
  }
  loading.value = false
}

const goListPage = (page) => {
  if (page < 1 || page > listTotalPages.value || page === listPage.value) return
  loadPages(page)
}

let searchTimer = null
watch(searchQuery, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    listPage.value = 1
    loadPages(1)
  }, 300)
})

const normalizeList = (data) => Array.isArray(data) ? data : (data?.results || data?.data || data?.items || [])

const autoTagInput = ref('')
const allCustomerTags = ref([])
const tagSuggestions = computed(() => {
  const q = autoTagInput.value.trim().toLowerCase()
  if (!q) return []
  const selected = (editForm.value.auto_tags_display || []).map(t => t.id)
  return allCustomerTags.value.filter(t => t.name.toLowerCase().includes(q) && !selected.includes(t.id)).slice(0, 8)
})

const addAutoTag = async (tagName) => {
  const name = (tagName || autoTagInput.value).trim()
  if (!name) return
  const existing = allCustomerTags.value.find(t => t.name.toLowerCase() === name.toLowerCase())
  const tags = editForm.value.auto_tags_display || []
  if (existing) {
    if (!tags.find(t => t.id === existing.id)) {
      editForm.value.auto_tags_display = [...tags, existing]
    }
  } else {
    // 创建新标签
    try {
      const res = await apiRequest.post('/api/customers/v2/tags/', { name })
      const newTag = res.data
      allCustomerTags.value.push(newTag)
      editForm.value.auto_tags_display = [...tags, newTag]
    } catch (e) { console.error('创建标签失败', e) }
  }
  autoTagInput.value = ''
}

const removeAutoTag = (tagId) => {
  editForm.value.auto_tags_display = (editForm.value.auto_tags_display || []).filter(t => t.id !== tagId)
}

const onTagKeydown = (e) => {
  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addAutoTag() }
}
const loadOptions = async () => {
  try {
    const [accRes, grpRes, tagRes] = await Promise.all([
      apiRequest.get('/api/email-accounts/v2/accounts/?page_size=1000'),
      apiRequest.get('/api/customers/v2/groups/?page_size=1000'),
      apiRequest.get('/api/customers/v2/tags/?page_size=1000'),
    ])
    emailAccounts.value = normalizeList(accRes.data)
    customerGroups.value = normalizeList(grpRes.data)
    allCustomerTags.value = normalizeList(tagRes.data)
  } catch (e) { console.error('加载PRO页面选项失败:', e) }
}

const selectPage = async (page) => {
  loadingPageId.value = page.id
  try {
    const res = await qrCodeProService.getPage(page.id)
    if (res.success) {
      currentPage.value = res.data
      editForm.value = { ...res.data }
      if (!editForm.value.landing_content || !Object.keys(editForm.value.landing_content).length) {
        editForm.value.landing_content = JSON.parse(JSON.stringify(defaultContent))
      } else {
        // 补全各语言缺失字段
        for (const lang of Object.keys(defaultContent)) {
          editForm.value.landing_content[lang] = Object.assign({}, defaultContent[lang], editForm.value.landing_content[lang] || {})
        }
      }
      currentLangContent.value = editForm.value.landing_content[editLang.value] || {}
      ensureBlocks(currentLangContent.value)
      // 默认选中第一个批次
      if (res.data.batches && res.data.batches.length) {
        selectBatch(res.data.batches[0])
      } else {
        currentBatch.value = null
        batchContentCurrent.value = {}
      }
      contentEditMode.value = 'blocks'
      loadSubscriptions(page.id)
    }
  } finally {
    loadingPageId.value = null
  }
}

const loadSubscriptions = async (pageId) => {
  const res = await qrCodeProService.getSubscriptions(pageId)
  if (res.success) subscriptions.value = res.data || []
}

const deletePage = async (page) => {
  if (!confirm(`确定删除「${page.name}」？删除后页面、批次、二维码全部失效（已订阅客户保留在客户管理）。`)) return
  const res = await qrCodeProService.deletePage(page.id)
  if (res.success) {
    toast.success('删除成功')
    if (currentPage.value?.id === page.id) {
      currentPage.value = null
      editForm.value = {}
    }
    const nextPage = allPages.value.length <= 1 && listPage.value > 1 ? listPage.value - 1 : listPage.value
    await loadPages(nextPage)
  } else {
    toast.error(res.message || '删除失败')
  }
}

const duplicatePage = async (page) => {
  if (!confirm(`复制「${page.name}」？将连同所有批次和自定义内容一起复制（统计数据、域名不复制）。`)) return
  const res = await qrCodeProService.duplicatePage(page.id)
  if (res.success) {
    toast.success('页面已复制')
    await loadPages(1)
    if (res.data?.id) selectPage(res.data)
  } else {
    toast.error(res.message || '复制失败')
  }
}

const savePage = async () => {
  updateLandingContent()
  // 确保所有语言都有 blocks
  if (editForm.value.landing_content) {
    for (const lang of Object.keys(editForm.value.landing_content)) {
      ensureBlocks(editForm.value.landing_content[lang])
    }
  }
  const res = await qrCodeProService.updatePage(currentPage.value.id, {
    name: editForm.value.name, amazon_asin: editForm.value.amazon_asin,
    review_link: editForm.value.review_link, verification_code: editForm.value.verification_code,
    whatsapp_number: editForm.value.whatsapp_number, customer_source: editForm.value.customer_source,
    send_email_to_china: editForm.value.send_email_to_china !== false,
    china_detect_enabled: editForm.value.china_detect_enabled !== false,
    china_detect_by_language: editForm.value.china_detect_by_language !== false,
    china_detect_by_ip: editForm.value.china_detect_by_ip === true,
    china_detect_by_timezone: editForm.value.china_detect_by_timezone === true,
    china_detect_by_ua: editForm.value.china_detect_by_ua === true,
    china_detect_by_utc_offset: editForm.value.china_detect_by_utc_offset === true,
    china_detect_by_nav_languages: editForm.value.china_detect_by_nav_languages === true,
    china_detect_by_fonts: editForm.value.china_detect_by_fonts === true,
    china_detect_by_connectivity: editForm.value.china_detect_by_connectivity === true,
    china_ingest: editForm.value.china_ingest !== false,
    china_email_subject: editForm.value.china_email_subject || '',
    china_email_body: editForm.value.china_email_body || '',
    china_html_enabled: editForm.value.china_html_enabled === true,
    china_html: editForm.value.china_html || '',
    allow_duplicate_subscribe: editForm.value.allow_duplicate_subscribe === true,
    auto_redirect_page2: editForm.value.auto_redirect_page2 === true,

    landing_content: editForm.value.landing_content,
    sender_email_account: editForm.value.sender_email_account || null,
    auto_tag_ids: (editForm.value.auto_tags_display || []).map(t => t.id),
    customer_group: editForm.value.customer_group || null,
    custom_domain: editForm.value.custom_domain || '',
  })
  if (res.success) { toast.success('保存成功'); loadPages(); refreshPreview() }
  else toast.error(res.message)
}

const createPage = async () => {
  const res = await qrCodeProService.createPage({
    ...newPage.value, verification_code: '1688',
    landing_content: JSON.parse(JSON.stringify(defaultContent))
  })
  if (res.success) {
    toast.success('页面创建成功'); showCreateModal.value = false
    newPage.value = { name: '' }; await loadPages(1)
  } else toast.error(res.message)
}

const createBatch = async () => {
  if (!currentPage.value) return
  const res = await qrCodeProService.createBatch(currentPage.value.id, { ...newBatch.value, page: currentPage.value.id })
  if (res.success) {
    toast.success('批次创建成功'); showBatchModal.value = false
    newBatch.value = { name: '', shipment_quantity: 0, notes: '' }; selectPage(currentPage.value)
  } else toast.error(res.message)
}

const deleteBatch = async (batch) => {
  if (!confirm(`确定删除批次「${batch.name}」？此操作不可恢复。`)) return
  const res = await qrCodeProService.deleteBatch(currentPage.value.id, batch.id)
  if (res.success) { toast.success('批次已删除'); selectPage(currentPage.value) }
  else toast.error(res.message || '删除失败')
}

const duplicateBatch = async (batch) => {
  if (!confirm(`复制批次「${batch.name}」？将连同自定义内容、标签一起复制（统计数据清零）。`)) return
  const res = await qrCodeProService.duplicateBatch(batch.id)
  if (res.success) { toast.success('批次已复制'); selectPage(currentPage.value) }
  else toast.error(res.message || '复制失败')
}

const openEditBatch = (batch) => {
  editBatchForm.value = {
    id: batch.id,
    name: batch.name,
    shipment_quantity: batch.shipment_quantity || 0,
    notes: batch.notes || '',
  }
  showEditBatchModal.value = true
}

const saveEditBatch = async () => {
  const res = await qrCodeProService.updateBatch(currentPage.value.id, editBatchForm.value.id, {
    name: editBatchForm.value.name,
    shipment_quantity: editBatchForm.value.shipment_quantity,
    notes: editBatchForm.value.notes,
  })
  if (res.success) {
    toast.success('批次信息已保存'); showEditBatchModal.value = false
    selectPage(currentPage.value)
  } else toast.error(res.message || '保存失败')
}

// 批次是否已有自定义内容（任一语言任一字段非空）
const hasBatchContent = (batch) => {
  const lc = batch.landing_content
  if (!lc) return false
  return Object.values(lc).some(langObj => langObj && Object.values(langObj).some(v => v !== null && v !== ''))
}

// 取页面当前语言某字段的默认值（用作 placeholder）
const pageDefault = (field) => {
  const lc = editForm.value.landing_content || {}
  const c = lc[editLang.value] || lc['en'] || {}
  return c[field] || '（继承页面）'
}

// 选中批次 → 加载批次内容到编辑区
const selectBatch = async (batch) => {
  if (loadingBatchId.value) return
  loadingBatchId.value = batch.id
  try {
    // 先让 loading 动画渲染出来，再做切换（与第一栏页面列表切换效果一致）
    await new Promise(r => setTimeout(r, 300))
    // 先把旧批次的编辑回写
    if (currentBatch.value && currentBatch.value._editContent) {
      currentBatch.value._editContent[editLang.value] = { ...batchContentCurrent.value }
    }
    currentBatch.value = batch
    // 存一份工作副本
    if (!batch._editContent) {
      batch._editContent = JSON.parse(JSON.stringify(batch.landing_content || {}))
    }
    batchContentCurrent.value = { ...(batch._editContent[editLang.value] || {}) }
    // 如果批次没有 blocks，用页面内容初始化默认模块
    if (!batchContentCurrent.value.blocks || !batchContentCurrent.value.blocks.length) {
      const pageContent = (editForm.value.landing_content || {})[editLang.value] || {}
      ensureBlocks(batchContentCurrent.value, pageContent)
    }
    if (!batchContentCurrent.value.page2_blocks || !batchContentCurrent.value.page2_blocks.length) {
      const pageContent = (editForm.value.landing_content || {})[editLang.value] || {}
      ensurePage2Blocks(batchContentCurrent.value, pageContent)
    }
  } finally {
    loadingBatchId.value = null
  }
}

// 切语言时同步批次内容
watch(editLang, (newLang, oldLang) => {
  // 页面内容同步（原逻辑）
  if (editForm.value.landing_content) {
    currentLangContent.value = editForm.value.landing_content[newLang] || {}
    ensureBlocks(currentLangContent.value)
  }
  // 批次内容同步
  if (currentBatch.value && currentBatch.value._editContent) {
    currentBatch.value._editContent[oldLang] = { ...batchContentCurrent.value }
    batchContentCurrent.value = { ...(currentBatch.value._editContent[newLang] || {}) }
    if (!batchContentCurrent.value.blocks || !batchContentCurrent.value.blocks.length) {
      const pageContent = (editForm.value.landing_content || {})[newLang] || {}
      ensureBlocks(batchContentCurrent.value, pageContent)
    }
    if (!batchContentCurrent.value.page2_blocks || !batchContentCurrent.value.page2_blocks.length) {
      const pageContent = (editForm.value.landing_content || {})[newLang] || {}
      ensurePage2Blocks(batchContentCurrent.value, pageContent)
    }
  }
})

const saveBatchContent = async () => {
  if (!currentBatch.value) return
  // 回写当前语言
  if (!currentBatch.value._editContent) currentBatch.value._editContent = {}
  currentBatch.value._editContent[editLang.value] = { ...batchContentCurrent.value }
  // 清理空字段
  const cleaned = {}
  for (const [lang, obj] of Object.entries(currentBatch.value._editContent || {})) {
    const filtered = {}
    for (const [k, v] of Object.entries(obj || {})) {
      if (v !== null && v !== '') filtered[k] = v
    }
    if (Object.keys(filtered).length) cleaned[lang] = filtered
  }
  const res = await qrCodeProService.updateBatch(currentPage.value.id, currentBatch.value.id, { landing_content: cleaned })
  if (res.success) {
    toast.success('批次内容已保存')
    // 更新本地数据
    currentBatch.value.landing_content = cleaned
    currentBatch.value._editContent = JSON.parse(JSON.stringify(cleaned))
    refreshPreview()
  } else toast.error(res.message || '保存失败')
}

const getHostRecord = (domain) => {
  if (!domain) return 'www'
  const parts = domain.split('.')
  return parts.length > 2 ? parts.slice(0, -2).join('.') : '@'
}

const openDomainModal = async () => {
  if (!currentPage.value) return
  if (editForm.value.custom_domain) {
    modalDomain.value = editForm.value.custom_domain
    if (editForm.value.domain_verified && !editForm.value.ssl_enabled) {
      modalStep.value = 3
      await refreshDomainStatus()
    } else if (editForm.value.domain_verified) {
      modalStep.value = 1
    } else {
      modalStep.value = 2
    }
  } else {
    modalDomain.value = ''
    modalStep.value = 1
  }
  showDomainModal.value = true
}

const closeDomainModal = () => {
  showDomainModal.value = false
  modalDomain.value = ''
  modalStep.value = 1
}

const clearDomain = async () => {
  if (!currentPage.value) return
  if (!confirm(`确定要清空自定义域名吗？\n\n当前域名：${editForm.value.custom_domain}`)) return
  editForm.value.custom_domain = ''
  await savePage()
  domainStatus.value = { verified: false, ssl_enabled: false, full_url: '' }
  await selectPage(currentPage.value)
}

const refreshDomainStatus = async () => {
  if (!currentPage.value?.id) return
  const res = await qrCodeProService.getDomainStatus(currentPage.value.id)
  if (res.success) {
    domainStatus.value = {
      verified: res.data.domain_verified || false,
      ssl_enabled: res.data.ssl_enabled || false,
      full_url: res.data.full_url || '',
    }
  }
}

const saveAndNextStep = async () => {
  if (!modalDomain.value) { toast.error('请输入域名'); return }
  isSavingDomain.value = true
  try {
    const res = await qrCodeProService.checkDomain(currentPage.value.id, modalDomain.value)
    if (res.success && res.available) {
      modalStep.value = 2
    } else {
      toast.error(res.message || '该域名已被占用')
    }
  } finally {
    isSavingDomain.value = false
  }
}

const verifyDomainInModal = async () => {
  isVerifyingDomain.value = true
  try {
    const saveRes = await qrCodeProService.updatePage(currentPage.value.id, {
      ...editForm.value, custom_domain: modalDomain.value
    })
    if (!saveRes.success) { toast.error('保存域名失败'); return }
    const res = await qrCodeProService.verifyDomain(currentPage.value.id)
    if (res.success === true && res.data?.verified === true) {
      editForm.value.custom_domain = modalDomain.value
      editForm.value.domain_verified = true
      await refreshDomainStatus()
      toast.success('DNS验证成功，请继续申请SSL')
      modalStep.value = 3
    } else {
      await qrCodeProService.updatePage(currentPage.value.id, { ...editForm.value, custom_domain: '' })
      await selectPage(currentPage.value)
      toast.error(res.message || '域名验证失败，请检查DNS配置')
    }
  } finally {
    isVerifyingDomain.value = false
  }
}

const applySSLInModal = async () => {
  isSettingUpSSL.value = true
  try {
    const res = await qrCodeProService.applySSL(currentPage.value.id)
    if (res.success) {
      toast.success('SSL证书申请已提交，预计需要1-2分钟')
      let retries = 0
      const check = setInterval(async () => {
        retries++
        if (retries > 20) { clearInterval(check); isSettingUpSSL.value = false; toast.error('SSL申请超时'); return }
        const s = await qrCodeProService.getDomainStatus(currentPage.value.id)
        if (s.success && s.data.ssl_enabled) {
          clearInterval(check); isSettingUpSSL.value = false
          toast.success('SSL证书已成功启用！')
          await refreshDomainStatus(); await selectPage(currentPage.value)
          closeDomainModal()
        }
      }, 6000)
    } else {
      toast.error(res.message || 'SSL申请失败')
      isSettingUpSSL.value = false
    }
  } catch (e) {
    toast.error('SSL申请失败'); isSettingUpSSL.value = false
  }
}

// 二维码放大
const qrZoom = ref({ show: false, url: '', name: '', landing_url: '' })
const openQrZoom = (batch) => {
  qrZoom.value = { show: true, url: batch.qr_code_url, name: batch.name, landing_url: batch.landing_url || '' }
}

const downloadQR = async (batch) => {
  const url = batch.qr_code_url || batch.url
  if (!url) return
  const a = document.createElement('a')
  a.href = url
  a.download = `qr-${batch.name || 'code'}.png`
  a.click()
}

const copyLink = (batch) => {
  const url = batch.landing_url || batch.qr_code_url || ''
  if (!url) return
  navigator.clipboard.writeText(url).then(() => toast.success('链接已复制')).catch(() => toast.error('复制失败'))
}

onMounted(() => { loadPages(); loadOptions() })
</script>
